import * as THREE from 'three';
import type { SimulationFrame } from '../physics/index.ts';
import type { TrackSampler } from '../track/index.ts';

export interface RideCameraSettings {
  readonly fieldOfView: number;
  readonly shake: number;
  readonly reducedMotion: boolean;
}

export const DEFAULT_RIDE_CAMERA_SETTINGS: RideCameraSettings = {
  fieldOfView: 62,
  shake: 0.25,
  reducedMotion: false,
};

export function normalizeRideCameraSettings(
  value: Partial<RideCameraSettings>,
  previous = DEFAULT_RIDE_CAMERA_SETTINGS,
): RideCameraSettings {
  return {
    fieldOfView: THREE.MathUtils.clamp(
      Number.isFinite(value.fieldOfView) ? value.fieldOfView! : previous.fieldOfView,
      50,
      75,
    ),
    shake: THREE.MathUtils.clamp(
      Number.isFinite(value.shake) ? value.shake! : previous.shake,
      0,
      1,
    ),
    reducedMotion: value.reducedMotion ?? previous.reducedMotion,
  };
}

const vector = (value: { readonly x: number; readonly y: number; readonly z: number }) =>
  new THREE.Vector3(value.x, value.y, value.z);

export class RideCameraController {
  private settings = DEFAULT_RIDE_CAMERA_SETTINGS;
  private needsSnap = true;
  private readonly worldUp = new THREE.Vector3(0, 1, 0);

  constructor(private readonly camera: THREE.PerspectiveCamera) {}

  setSettings(value: Partial<RideCameraSettings>): RideCameraSettings {
    this.settings = normalizeRideCameraSettings(value, this.settings);
    return this.snapshot;
  }

  get snapshot(): RideCameraSettings {
    return { ...this.settings };
  }

  snapNextFrame(): void {
    this.needsSnap = true;
  }

  updateRide(
    frame: SimulationFrame,
    sampler: TrackSampler,
    mode: 'first-person' | 'third-person',
  ): void {
    const lead = frame.cars[0];
    if (!lead) return;
    const position = vector(lead.position);
    const forward = vector(lead.forward).normalize();
    const carUp = vector(lead.up).normalize();
    const speed = Math.max(0, frame.referenceState.velocity);
    const future = sampler.sampleByDistance(
      lead.distance + THREE.MathUtils.clamp(speed * 0.82, 10, 24),
    );
    const futurePosition = vector(future.position);
    const stabilization = this.settings.reducedMotion ? 0.72 : 0.28;
    const cameraUp = carUp.clone().lerp(this.worldUp, stabilization).normalize();
    const shakeScale = this.settings.reducedMotion ? 0.12 : 1;
    const force = frame.forces[0]?.windows['200'] ?? frame.forces[0];
    const forceResponse = force
      ? THREE.MathUtils.clamp(
          (Math.abs(force.vertical - 1) + Math.abs(force.lateral) + Math.abs(force.longitudinal)) /
            5,
          0,
          1,
        )
      : 0;
    const shake =
      this.settings.shake *
      shakeScale *
      Math.min(1, speed / 35) *
      THREE.MathUtils.lerp(0.35, 1, forceResponse);
    const phase = frame.time * (10 + Math.min(18, speed * 0.45));
    const jitterRight = Math.sin(phase) * 0.035 * shake;
    const jitterUp = Math.sin(phase * 0.71 + 1.2) * 0.025 * shake;

    if (mode === 'first-person') {
      const desired = position
        .clone()
        .addScaledVector(carUp, 1.58)
        .addScaledVector(forward, 1.42)
        .addScaledVector(vector(lead.right), jitterRight)
        .addScaledVector(cameraUp, jitterUp);
      this.camera.position.copy(desired);
      this.camera.up.copy(cameraUp);
      this.camera.lookAt(futurePosition.addScaledVector(cameraUp, 0.32));
      this.applyFov(
        this.settings.fieldOfView + (this.settings.reducedMotion ? 0 : Math.min(4, speed / 18)),
      );
    } else {
      const tail = frame.cars.at(-1) ?? lead;
      const trainCenter = position.clone().lerp(vector(tail.position), 0.5);
      const trainLength = Math.max(4, frame.cars.length * 2.6);
      const flatForward = forward.clone();
      flatForward.y *= this.settings.reducedMotion ? 0.12 : 0.3;
      if (flatForward.lengthSq() < 1e-6) flatForward.set(0, 0, -1);
      flatForward.normalize();
      const chaseDistance = THREE.MathUtils.clamp(18 + trainLength * 0.55, 22, 31);
      const chaseHeight = THREE.MathUtils.clamp(7.5 + trainLength * 0.16, 8.5, 12);
      const desired = trainCenter
        .clone()
        .addScaledVector(this.worldUp, this.settings.reducedMotion ? chaseHeight + 1 : chaseHeight)
        .addScaledVector(
          flatForward,
          this.settings.reducedMotion ? -chaseDistance - 2 : -chaseDistance,
        )
        .addScaledVector(vector(lead.right), this.settings.reducedMotion ? 3.5 : 4.5);
      desired.y = Math.max(2.5, desired.y);
      if (this.needsSnap) this.camera.position.copy(desired);
      else this.camera.position.lerp(desired, this.settings.reducedMotion ? 0.065 : 0.115);
      this.camera.up.copy(this.worldUp);
      const chaseTarget = trainCenter
        .clone()
        .addScaledVector(carUp, 1)
        .addScaledVector(forward, 2.5);
      this.camera.lookAt(chaseTarget);
      this.applyFov(Math.max(52, this.settings.fieldOfView - 4));
    }
    this.needsSnap = false;
  }

  updateCinematic(sampler: TrackSampler, distance: number, time: number): void {
    const sample = sampler.sampleByDistance(distance);
    const target = vector(sample.position).addScaledVector(this.worldUp, 1.15);
    const tangent = vector(sample.tangent).normalize();
    const right = vector(sample.right).normalize();
    const shot = Math.floor(time / 3.5) % 3;
    const desired =
      shot === 0
        ? target.clone().addScaledVector(right, 20).addScaledVector(this.worldUp, 9)
        : shot === 1
          ? target.clone().addScaledVector(tangent, -24).addScaledVector(this.worldUp, 7)
          : target
              .clone()
              .addScaledVector(right, -15)
              .addScaledVector(tangent, 11)
              .addScaledVector(this.worldUp, 12);
    desired.y = Math.max(3, desired.y);
    if (this.needsSnap) this.camera.position.copy(desired);
    else this.camera.position.lerp(desired, this.settings.reducedMotion ? 0.055 : 0.09);
    this.camera.up.copy(this.worldUp);
    this.camera.lookAt(target);
    this.applyFov(this.settings.reducedMotion ? 54 : 58);
    this.needsSnap = false;
  }

  private applyFov(value: number): void {
    if (Math.abs(this.camera.fov - value) < 0.01) return;
    this.camera.fov = value;
    this.camera.updateProjectionMatrix();
  }
}
